import { useNavigate } from 'react-router-dom';
import { Icon } from '@/components/atoms';
import type { IconProps } from '@/components/atoms';

interface ReportCard {
  id: string;
  title: string;
  description: string;
  icon: IconProps['name'];
  href: string;
  color: 'primary' | 'success' | 'warning' | 'info' | 'error';
}

const reportCards: ReportCard[] = [
  {
    id: 'products',
    title: 'Products Report',
    description: 'Product catalog with variant counts, stock levels, and activity status',
    icon: 'Package',
    href: '/reports/products',
    color: 'primary',
  },
  {
    id: 'vendors',
    title: 'Vendors Report',
    description: 'Vendor information with contact details, GST status, and purchase values',
    icon: 'Users',
    href: '/reports/vendors',
    color: 'info',
  },
  {
    id: 'customers',
    title: 'Customers Report',
    description: 'Customer purchase history with revenue metrics and warehouse associations',
    icon: 'UserCheck',
    href: '/reports/customers',
    color: 'success',
  },
  {
    id: 'inventory',
    title: 'Inventory Report',
    description: 'Inventory batches with stock levels, valuations, and expiry tracking',
    icon: 'Box',
    href: '/reports/inventory',
    color: 'warning',
  },
  {
    id: 'purchases',
    title: 'Purchases Report',
    description: 'Purchase order history with payment status and vendor breakdown',
    icon: 'ShoppingBag',
    href: '/reports/purchases',
    color: 'primary',
  },
  {
    id: 'sales',
    title: 'Sales Report',
    description: 'Sales transactions with revenue, tax, margin analysis, and payment breakdown',
    icon: 'TrendingUp',
    href: '/reports/sales',
    color: 'success',
  },
  {
    id: 'returns',
    title: 'Returns Report',
    description:
      'Return transactions with refund amounts, status breakdown, and warehouse tracking',
    icon: 'RefreshCw',
    href: '/reports/returns',
    color: 'error',
  },
];

const colorClasses = {
  primary: {
    bg: 'bg-primary-50',
    icon: 'text-primary-600',
    hover: 'hover:border-primary-300 hover:shadow-primary-100',
  },
  success: {
    bg: 'bg-success-50',
    icon: 'text-success-600',
    hover: 'hover:border-success-300 hover:shadow-success-100',
  },
  warning: {
    bg: 'bg-warning-50',
    icon: 'text-warning-600',
    hover: 'hover:border-warning-300 hover:shadow-warning-100',
  },
  info: {
    bg: 'bg-info-50',
    icon: 'text-info-600',
    hover: 'hover:border-info-300 hover:shadow-info-100',
  },
  error: {
    bg: 'bg-error-50',
    icon: 'text-error-600',
    hover: 'hover:border-error-300 hover:shadow-error-100',
  },
};

export default function ReportsPage() {
  const navigate = useNavigate();

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold text-neutral-900">Reports</h1>
        <p className="text-neutral-600 mt-2">
          Generate and export business reports for analysis and insights
        </p>
      </div>

      {/* Report Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {reportCards.map((report) => {
          const colors = colorClasses[report.color];
          return (
            <button
              key={report.id}
              onClick={() => navigate(report.href)}
              className={`bg-white rounded-lg shadow-sm border border-neutral-200 p-6 text-left transition-all duration-200 ${colors.hover} hover:shadow-md`}
            >
              <div className={`${colors.bg} p-3 rounded-lg w-fit mb-4`}>
                <Icon name={report.icon} size={24} className={colors.icon} />
              </div>
              <h3 className="text-lg font-semibold text-neutral-900 mb-2">{report.title}</h3>
              <p className="text-sm text-neutral-600 line-clamp-2">{report.description}</p>
              <div className="mt-4 flex items-center text-sm font-medium text-primary-600">
                View Report
                <Icon name="ArrowRight" size={16} className="ml-1" />
              </div>
            </button>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div className="bg-gradient-to-br from-primary-600 to-primary-700 rounded-lg shadow-lg p-6 text-white">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Export Options</h3>
          <Icon name="Download" size={24} className="text-primary-200" />
        </div>
        <p className="text-primary-100 text-sm mb-4">
          All reports support multiple export formats including Excel (XLSX) and PDF for easy
          sharing and analysis.
        </p>
        <div className="flex gap-4">
          <div className="flex items-center gap-2 text-sm">
            <div className="bg-primary-500 p-1.5 rounded">
              <Icon name="FileSpreadsheet" size={16} />
            </div>
            <span>Excel Export</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <div className="bg-primary-500 p-1.5 rounded">
              <Icon name="FileText" size={16} />
            </div>
            <span>PDF Export</span>
          </div>
        </div>
      </div>
    </div>
  );
}
